import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { getActiveOrganization } from "selectors/organizations";
import { getPricesData } from "selectors/metaData";
import { getSelectedBusinessData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import Businesses from "utils/Businesses";
import Button from "nlib/ui/Button";
import Constants from "const/Constants";
import DateTimeFormats from "const/DateTimeFormats";
import IntegrationServices from "const/IntegrationServices";
import Modal from "nlib/ui/Modal";
import Money from "utils/Money";
import React, { useCallback, useMemo, useState } from "react";
import StripeActions from "actions/StripeActions";
import Texts from "utils/Texts";
import moment from "moment";

const TEXT_VAR_PATTERN = /{{(.*?)}}/g;

const TEXT_VAR_GROUP_SIZE = 2;

const renderTextWithVars = (text, vars, className) => {
  return text.split(TEXT_VAR_PATTERN).map((item, index) => {
    if (index % TEXT_VAR_GROUP_SIZE === 0) return item;

    return (
      <span key={item} className={className}>
        {Object.prototype.hasOwnProperty.call(vars, item) ? vars[item] : `{{${item}}}`}
      </span>
    );
  });
};

const UpsellModal = ({ onClose }) => {
  const dispatch = useDispatch();

  const { uiTexts, messages } = useSelector(getTextsData);

  const activeOrganization = useSelector(getActiveOrganization);

  const { oneTimePrices: { roboticUpsell: roboticUpsellPrices = {} } = {} } = useSelector(getPricesData);

  const {
    id: businessId,
    createdAt,
    settings: {
      rpaStartFrom
    } = {},
    extraData: {
      integrationService,
      quickBooksBookCloseDate,
      quickBooksOldestTransactionDate,
      rpaUpsellDate
    } = {}
  } = useSelector(getSelectedBusinessData);

  const bookCloseDate = useMemo(() => {
    return Businesses.getBookCloseDate(quickBooksBookCloseDate);
  }, [quickBooksBookCloseDate]);

  const oldestTransactionDate = useMemo(() => {
    return quickBooksOldestTransactionDate && moment(quickBooksOldestTransactionDate).isValid()
      ? moment(quickBooksOldestTransactionDate).startOf("month")
      : null;
  }, [quickBooksOldestTransactionDate]);

  const openBookDate = useMemo(() => {
    return bookCloseDate
      ? moment(bookCloseDate).add(1, "month").startOf("month")
      : null;
  }, [bookCloseDate]);

  const closedBooksAffectUnlock = useMemo(() => {
    return !!(
      oldestTransactionDate
      && openBookDate
      && openBookDate.isAfter(oldestTransactionDate)
    );
  }, [oldestTransactionDate, openBookDate]);

  const upsellDate = useMemo(() => {
    return Businesses.getUpsellDate(quickBooksOldestTransactionDate, bookCloseDate);
  }, [quickBooksOldestTransactionDate, bookCloseDate]);

  const currentRpaStartFromDate = useMemo(() => {
    if (rpaStartFrom && moment(rpaStartFrom).isValid()) {
      return moment(rpaStartFrom);
    }

    return Businesses.getRpaStartFromMinDate(
      integrationService,
      rpaUpsellDate,
      createdAt
    );
  }, [integrationService, rpaStartFrom, rpaUpsellDate, createdAt]);

  const heroText = useMemo(() => {
    const [
      upsellModalIntro,
      upsellModalDescription,
      upsellModalClosedBooksDescription
    ] = messages.upsellModalDescription;

    return renderTextWithVars(
      `${upsellModalIntro} ${closedBooksAffectUnlock ? upsellModalClosedBooksDescription : upsellModalDescription}`,
      {
        oldestReviewDate: oldestTransactionDate?.format(DateTimeFormats.MONTH_AND_YEAR_TEXT_EXT),
        closedBookDate: bookCloseDate?.format(DateTimeFormats.MONTH_AND_YEAR_TEXT_EXT),
        openBookDate: openBookDate?.format(DateTimeFormats.MONTH_AND_YEAR_TEXT_EXT),
        currentReviewDate: currentRpaStartFromDate.format(DateTimeFormats.MONTH_AND_YEAR_TEXT_EXT)
      },
      Css.heroTextEmphasis
    );
  }, [
    messages,
    bookCloseDate,
    openBookDate,
    closedBooksAffectUnlock,
    oldestTransactionDate,
    currentRpaStartFromDate
  ]);

  const upsellPriceText = useMemo(() => {
    const organizationCurrency = activeOrganization?.currency || Constants.DEFAULT_CURRENCY;

    const price = roboticUpsellPrices[organizationCurrency] || roboticUpsellPrices[Constants.DEFAULT_CURRENCY];

    return price
      ? Money.toMoneyString(
        price,
        roboticUpsellPrices[organizationCurrency] ? organizationCurrency : Constants.DEFAULT_CURRENCY
      )
      : Constants.EMPTY_PLACEHOLDER;
  }, [activeOrganization, roboticUpsellPrices]);

  const [redirecting, setRedirecting] = useState(false);

  const handleUnlock = useCallback(async() => {
    setRedirecting(true);

    const url = await dispatch(StripeActions.createUpsellCheckoutSession(businessId));

    if (url) {
      window.location.assign(url);

      return;
    }
    setRedirecting(false);
  }, [businessId, dispatch]);

  if (integrationService !== IntegrationServices.QUICK_BOOKS.value || !upsellDate || !oldestTransactionDate) return null;

  const [
    upsellModalFeatureDescriptionA,
    upsellModalFeatureDescriptionB,
    upsellModalFeatureDescriptionC
  ] = messages.upsellModalFeatureList;

  return (
    <Modal
      notClosable={redirecting}
      className={Css.upsellModal}
      dialogClassName={Css.dialog}
      headerClassName={Css.header}
      contentClassName={Css.content}
      iconComponent={null}
      title={null}
      size="lg"
      onClose={onClose}>
      <div className={Css.hero}>
        <div className={Css.heroTitle}>{uiTexts.catchUpBookkeeping}</div>
        <div className={Css.heroText}>{heroText}</div>
      </div>
      {closedBooksAffectUnlock && (
        <div className={Css.details}>
          <div className={Css.detailItem}>
            <div className={`${Css.detailIcon} ${Css.warning}`}>
              <Icons.ClockCounterClockwise />
            </div>
            <div className={Css.detailContent}>
              <div className={Css.detailLabel}>{uiTexts.earliestReviewDate}</div>
              <div className={Css.detailValue}>
                {oldestTransactionDate.format(DateTimeFormats.MONTH_AND_YEAR_TEXT_EXT)}
              </div>
            </div>
          </div>
          <div className={Css.detailItem}>
            <div className={`${Css.detailIcon} ${Css.positive}`}>
              <Icons.LockOpen />
            </div>
            <div className={Css.detailContent}>
              <div className={Css.detailLabel}>{uiTexts.openBookDate}</div>
              <div className={Css.detailValue}>
                {openBookDate.format(DateTimeFormats.MONTH_AND_YEAR_TEXT_EXT)}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className={Css.actionCard}>
        <div className={Css.cardTitle}>
          {Texts.replaceVars(messages.upsellModalCardTitle, {
            date: upsellDate.format(DateTimeFormats.MONTH_AND_YEAR_TEXT_EXT)
          })}
        </div>
        <div className={Css.cardInfoRow}>
          <div className={Css.cardText}>{messages.upsellModalCardDescription}</div>
          <div className={Css.priceBanner}>
            <div className={Css.priceHeader}>
              <div className={Css.cardEyebrow}>
                <Icons.Receipt />
                <span>{uiTexts.oneTimePayment}</span>
              </div>
              <div className={Css.priceAmount}>{upsellPriceText}</div>
            </div>
          </div>
        </div>
        <div className={Css.list}>
          <div className={Css.listItem}>
            <Icons.CheckCircle />
            <span>{Texts.replaceVars(upsellModalFeatureDescriptionA, {
              date: upsellDate.format(DateTimeFormats.MONTH_AND_YEAR_TEXT_EXT)
            })}</span>
          </div>
          <div className={Css.listItem}>
            <Icons.CheckCircle />
            <span>{Texts.replaceVars(upsellModalFeatureDescriptionB, {
              date: upsellDate.format(DateTimeFormats.MONTH_AND_YEAR_TEXT_EXT)
            })}</span>
          </div>
          <div className={Css.listItem}>
            <Icons.CheckCircle />
            <span>{upsellModalFeatureDescriptionC}</span>
          </div>
        </div>
        <div className={Css.actions}>
          <div className={Css.footerNote}>
            <span>{messages.upsellSettingsNote}</span>{" "}
            <span className={Css.footerPath}>
              <span className={Css.footerPathItem}>{uiTexts.businessSettings}</span>
              <span className={Css.footerPathSeparator}>&gt;</span>
              <span className={Css.footerPathItem}>{uiTexts.roboticAiBookkeeper}</span>
            </span>
          </div>
          <div className={Css.buttonsRow}>
            <Button
              outline
              large
              disabled={redirecting}
              className={Css.secondaryButton}
              onClick={onClose}>
              {uiTexts.notNow}
            </Button>
            <Button
              large
              primary
              loading={redirecting}
              className={Css.primaryButton}
              onClick={handleUnlock}>
              {redirecting ? `${uiTexts.redirectingPleaseWait}...` : uiTexts.unlockEarlierPeriods}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(UpsellModal);
